import {
  Box,
  Button,
  Card,
  ChakraProvider,
  Grid,
  HStack,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Stack,
  TableContainer,
  Tfoot,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";

import { endOfMonth, format, parseISO } from "date-fns";
import DashboardDropdown from "./dashboardDateComponent";

import {
  DownloadIcon,
  SearchIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@chakra-ui/icons";
import {
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { useEffect, useRef, useState } from "react";
import { Menu as MenuIcon } from "react-feather";
import {
  getAccountListing,
  getTransactions,
  getTransactionsExport,
  getVendorsAnalysis,
  getVendorsAnalysisExport,
} from "../../api/vendorAnalysis";
import { customCurrencyFormat } from "../../utils";
import AccountSelect from "../dashboardComponents/vendorAnalytics/accountMenuSelect";
import Scrollbar from "../generic/scrollBar";
import HorizontalScrollbar from "../generic/scrollBarX";
import { useCurrentProfile } from "../../ContextWrapper";
import { getCustomerDashboardDate } from "../../utils/getCustomerDashboardDate";
import ClientDashboardLayout from "./dashboardLayout";
import theme from "../../theme";
import { fallbackDate } from "../../config/dashboardDefaults";
import axios from "axios";

export const VendorTable = ({
  customer_id,
  dashDate,
  updateDate,
  dateCategory,
  setDateCategory,
  initialDashDate,
  metric = false,
  tag = null,
  h = "670px",
}) => {
  const formattedDate = format(dashDate, "yyyy-MM-dd");
  const contentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [hasXOverflow, setHasXOverflow] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [vendorData, setVendorData] = useState({});
  const [transactionData, setTransactionData] = useState({});
  const [accountData, setAccountData] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(false);
  const [selectedVendorName, setSelectedVendorName] = useState(null);
  const [selectedAccountName, setSelectedAccountName] =
    useState("All Accounts");

  const [selectedVendorAccount, setSelectedVendorAccount] =
    useState("All Accounts");

  const handleExport = async (format) => {
    try {
      const params = {
        search: search,
        account: selectedAccount,
        tag: tag,
        export_format: format,
      };

      const controller = new AbortController();
      const signal = controller.signal;

      const response = await getVendorsAnalysisExport(
        customer_id,
        formattedDate,
        params,
        signal
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Vendor Analysis ${
        tag
          ? tag === "overhead"
            ? "operations"
            : tag === "cogs"
            ? "products"
            : tag
          : selectedAccountName
      }.${format === "excel" ? "xlsx" : format}`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  const inputRef = useRef();

  const handleSearch = () => {
    if (inputRef.current) {
      setSearch(inputRef.current.value);
    }
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
    }

    const checkOverflow = () => {
      if (contentRef.current && scrollContainerRef.current) {
        const hasOverflowX =
          contentRef.current.scrollWidth > contentRef.current.clientWidth;
        setHasXOverflow(hasOverflowX);

        if (hasOverflowX) {
          const contentWidth = contentRef.current.scrollWidth;
          const visibleWidth = scrollContainerRef.current.clientWidth;

          const thumbWidth = (visibleWidth / contentWidth) * 100;
          setScrollWidth(thumbWidth);
        } else {
          setScrollWidth(0);
        }
      }
    };

    checkOverflow();

    window.addEventListener("resize", checkOverflow);

    return () => {
      observer.disconnect();
    };
  }, [vendorData]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const hasOverflowY =
          contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setHasOverflow(hasOverflowY);

        if (hasOverflowY) {
          const contentHeight = contentRef.current.scrollHeight;
          const visibleHeight = contentRef.current.clientHeight;
          setScrollHeight((visibleHeight / contentHeight) * 100);
        } else {
          setScrollHeight(0);
        }
      }
    };

    checkOverflow();

    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    window.addEventListener("resize", checkOverflow);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [vendorData]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchVendors = async () => {
      try {
        const params = { search: search, account: selectedAccount, tag: tag };
        const res = await getVendorsAnalysis(
          customer_id,
          formattedDate,
          params,
          controller.signal
        );
        if (isMounted) {
          setVendorData(res.data);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error fetching vendors:", err);
        }
      }
    };

    if (customer_id && formattedDate) {
      fetchVendors();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, search, selectedAccount, tag]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchTransactions = async () => {
      try {
        const res = await getTransactions(
          customer_id,
          formattedDate,
          selectedVendor,
          selectedAccountId,
          controller.signal
        );
        if (isMounted) {
          setTransactionData(res.data);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error fetching metric summary:", err);
        }
      }
    };

    if (customer_id && formattedDate && selectedVendor) {
      fetchTransactions();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [customer_id, formattedDate, selectedVendor, selectedAccountId]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchAccounts = async () => {
      try {
        const res = await getAccountListing(customer_id, controller.signal);
        if (isMounted) {
          setAccountData(res.data);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error("Error fetching metric summary:", err);
        }
      }
    };

    if (customer_id) {
      fetchAccounts();
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [customer_id]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "desc",
  });

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!vendorData?.vendors) return [];
    const sorted = [...vendorData.vendors];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (typeof aVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    }
    return sorted;
  };

  const sortedVendors = getSortedData();

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <TriangleDownIcon w={6} h={3} />;
    }
    return sortConfig.direction === "asc" ? (
      <TriangleDownIcon w={6} h={3} />
    ) : (
      <TriangleUpIcon w={6} h={3} />
    );
  };

  return (
    <Box>
      <TransactionModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Vendor Transactions"
        transactionData={transactionData}
        vendor={selectedVendorName}
        account={selectedVendorAccount}
        vendorId={selectedVendor}
        formattedDate={formattedDate}
        customerId={customer_id}
        accountId={selectedAccountId}
      />
      <Stack>
        {!metric && (
          <Flex mb={4} flexWrap="wrap" align="center" gap={4} minWidth="0">
            <InputGroup
              border="1px"
              borderColor="gray.300"
              borderRadius="md"
              overflow="hidden"
              flexShrink={0}
              minW="250px"
              maxW="320px"
              bg={"#FFFFFF"}
            >
              <Input
                ref={inputRef}
                placeholder="Search vendor name..."
                border="none"
                _focus={{ boxShadow: "none" }}
              />
              <InputRightElement>
                <IconButton
                  aria-label="Search"
                  icon={<SearchIcon />}
                  bg={"#536DEC"}
                  borderLeftRadius="0"
                  borderTopLeftRadius="0"
                  borderBottomLeftRadius="0"
                  height="100%"
                  onClick={handleSearch}
                />
              </InputRightElement>
            </InputGroup>

            <Spacer />

            <DashboardDropdown
              key={dashDate?.toISOString()}
              height="36px"
              setEnd={updateDate}
              dateFromNav={initialDashDate}
              endDate={dashDate}
              category={dateCategory}
              setCategory={setDateCategory}
              includeAllFromMin={true}
            />

            <AccountSelect
              options={accountData}
              selectedId={selectedAccount}
              setSelectedId={setSelectedAccount}
              selectedName={selectedAccountName}
              setSelectedName={setSelectedAccountName}
            />

            <Menu isLazy placement="bottom-start" portal={false}>
              <MenuButton
                as={Button}
                leftIcon={<DownloadIcon />}
                bg="#627CFB"
                color="white"
                flexShrink={0}
                minW="fit-content"
                height="36px"
                _hover={{ bg: "#536DEC" }}
                _active={{ bg: "#435CD7" }}
              >
                Export
              </MenuButton>
              <MenuList
                border="1px solid #D1D4DA"
                bg="white"
                h="100%"
                overflowY="auto"
                width="103px"
                minW="unset"
                maxW="170px"
                left="50%"
                transform="translateX(-50%)"
                p={2}
              >
                {["csv", "xlsx", "pdf"].map((ext) => (
                  <MenuItem
                    key={ext}
                    bg="white"
                    color="black"
                    fontWeight="normal"
                    borderRadius="8px"
                    _hover={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    _focus={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    _active={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    onClick={() => handleExport(ext)}
                  >
                    .{ext}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        )}
        {metric && (
          <Flex my={"2px"} flexWrap="wrap" align="center" gap={4} minWidth="0">
            <Text fontWeight={700} fontSize="24px">
              Vendor Breakdown
            </Text>
            <Spacer />
            <Menu isLazy placement="bottom-start" portal={false}>
              <MenuButton
                as={Button}
                leftIcon={<DownloadIcon />}
                bg="#627CFB"
                color="white"
                flexShrink={0}
                minW="fit-content"
                height="36px"
                _hover={{ bg: "#536DEC" }}
                _active={{ bg: "#435CD7" }}
              >
                Export
              </MenuButton>
              <MenuList
                border="1px solid #D1D4DA"
                bg="white"
                h="100%"
                overflowY="auto"
                width="103px"
                minW="unset"
                maxW="170px"
                left="50%"
                transform="translateX(-50%)"
                p={2}
              >
                {["csv", "xlsx", "pdf"].map((ext) => (
                  <MenuItem
                    key={ext}
                    bg="white"
                    color="black"
                    fontWeight="normal"
                    borderRadius="8px"
                    _hover={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    _focus={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    _active={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    onClick={() => handleExport(ext)}
                  >
                    .{ext}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        )}
        <Stack align={"center"} pt={2} pb={2}>
          <Flex flexDirection={"column"} position="relative" width="100%">
            <Card
              borderRadius="12px"
              outlineColor={"#D1D4DA"}
              marginRight={hasOverflow ? "20px" : 0}
            >
              <TableContainer
                ref={contentRef}
                maxHeight={h}
                borderRadius="12px"
                position="relative"
                overflowY="auto"
                sx={{
                  "::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                onWheel={(e) => e.stopPropagation()}
              >
                <Table
                  variant="unstyled"
                  bg="white"
                  rounded="12px"
                  sx={{
                    tableLayout: "auto",
                    wordWrap: "break-word",
                    "th, td": {
                      minWidth: "208px",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                  }}
                >
                  <colgroup>
                    <col style={{ width: "19%" }} />
                    <col style={{ width: "19%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "15%" }} />
                  </colgroup>
                  <Thead position="sticky" top={0} zIndex={0} bg="#171923">
                    <Tr
                      fontSize="18px"
                      sx={{
                        "& td": {
                          color: "white",
                          fontWeight: "200",
                        },
                      }}
                    >
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() => handleSort("name")}
                        >
                          <Text textAlign="left" mr={2} fontWeight={700}>
                            Vendor Name
                          </Text>
                          {renderSortIcon("name")}
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() => handleSort("account")}
                        >
                          <Text textAlign="left" mr={2} fontWeight={700}>
                            Lucro Account
                          </Text>
                          {renderSortIcon("account")}
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() => handleSort("current_month_total")}
                        >
                          <Text textAlign="left" mr={2} fontWeight={700}>
                            Current Month
                          </Text>
                          {renderSortIcon("current_month_total")}
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() => handleSort("previous_month_total")}
                        >
                          <Text textAlign="left" mr={2} fontWeight={700}>
                            Previous Month
                          </Text>
                          {renderSortIcon("previous_month_total")}
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() => handleSort("ytd_total")}
                        >
                          <Text textAlign="left" mr={2} fontWeight={700}>
                            YTD Total
                          </Text>
                          {renderSortIcon("ytd_total")}
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          cursor="pointer"
                          onClick={() => handleSort("ytd_average")}
                        >
                          <Text textAlign="left" mr={2} fontWeight={700}>
                            YTD Average
                          </Text>
                          {renderSortIcon("ytd_average")}
                        </Flex>
                      </Td>
                    </Tr>
                  </Thead>

                  <Tbody zIndex={1}>
                    {sortedVendors.length > 0 ? (
                      sortedVendors.map((vendor) => (
                        <Tr
                          key={vendor.key}
                          height="20px"
                          cursor={
                            vendor?.name !== "Uncategorized"
                              ? "pointer"
                              : "default"
                          }
                          _hover={
                            vendor?.name !== "Uncategorized"
                              ? {
                                  border: "2px solid #536DEC",
                                  color: "#536DEC",
                                  fontWeight: "600",
                                }
                              : {}
                          }
                          onClick={() => {
                            if (vendor?.name !== "Uncategorized") {
                              setIsOpen(true);
                              setSelectedVendor(vendor.id);
                              setSelectedVendorName(vendor.name);
                              setSelectedAccountId(vendor.account_id);
                              setSelectedVendorAccount(vendor.account);
                            }
                          }}
                        >
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p="2px 10px"
                            h="33px"
                          >
                            <HStack>
                              <Flex align="center" justify="center">
                                {vendor?.name !== "Uncategorized"
                                  ? vendor.name
                                  : ""}
                              </Flex>
                            </HStack>
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p="2px 10px"
                            h="33px"
                          >
                            <HStack>
                              <Flex align="center" justify="center">
                                {vendor.account}
                              </Flex>
                            </HStack>
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p="2px 10px"
                            h="33px"
                          >
                            {customCurrencyFormat(
                              vendor?.current_month_total,
                              false,
                              true,
                              true,
                              true
                            )}
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p="2px 10px"
                            h="33px"
                          >
                            {customCurrencyFormat(
                              vendor.previous_month_total,
                              false,
                              true,
                              true,
                              true
                            )}
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p="2px 10px"
                            h="33px"
                          >
                            {customCurrencyFormat(
                              vendor.ytd_total,
                              false,
                              true,
                              true,
                              true
                            )}
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p="2px 10px"
                            h="33px"
                          >
                            {customCurrencyFormat(
                              vendor.ytd_average,
                              false,
                              true,
                              true,
                              true
                            )}
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr
                        border="1px solid"
                        borderColor="#D1D4DA"
                        height="20px"
                      >
                        <Td colSpan={7} textAlign="center">
                          No vendor data.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>

                  <Tfoot position="sticky" bottom={0} zIndex={0} bg="#171923">
                    <Tr
                      fontSize="18px"
                      sx={{
                        "& td": {
                          color: "white",
                          fontWeight: "200",
                        },
                      }}
                    >
                      <Td>Total</Td>
                      <Td></Td>
                      <Td>
                        {customCurrencyFormat(
                          vendorData?.totals
                            ? vendorData.totals.current_month_total
                            : "0",
                          false,
                          true,
                          true,
                          true
                        )}
                      </Td>
                      <Td>
                        {customCurrencyFormat(
                          vendorData?.totals
                            ? vendorData.totals.previous_month_total
                            : "0",
                          false,
                          true,
                          true,
                          true
                        )}
                      </Td>
                      <Td>
                        {customCurrencyFormat(
                          vendorData?.totals
                            ? vendorData.totals.ytd_total
                            : "0",
                          false,
                          true,
                          true,
                          true
                        )}
                      </Td>
                      <Td>
                        {customCurrencyFormat(
                          vendorData?.totals
                            ? vendorData.totals.ytd_average_total
                            : "0",
                          false,
                          true,
                          true,
                          true
                        )}
                      </Td>
                    </Tr>
                  </Tfoot>
                </Table>
              </TableContainer>
            </Card>
            {hasOverflow ? (
              <Box>
                <Scrollbar
                  targetRef={contentRef}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: "100%",
                    visibility: hasOverflow ? "visible" : "hidden",
                    "&::-webkit-scrollbar-thumb": {
                      height: `${scrollHeight}%`,
                      visibility: hasOverflow ? "visible" : "hidden",
                    },
                    "&::-webkit-scrollbar-track": {
                      visibility: hasOverflow ? "visible" : "hidden",
                    },
                  }}
                />
              </Box>
            ) : (
              <></>
            )}
          </Flex>
          <Box
            position={"relative"}
            width={"100%"}
            marginTop={"10px"}
            ref={scrollContainerRef}
          >
            {hasXOverflow ? (
              <HorizontalScrollbar
                targetRef={contentRef}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  visibility: hasXOverflow ? "visible" : "hidden",
                  "&::-webkit-scrollbar-thumb": {
                    width: `${scrollWidth}%`,
                    visibility: hasXOverflow ? "visible" : "hidden",
                  },
                  "&::-webkit-scrollbar-track": {
                    visibility: hasXOverflow ? "visible" : "hidden",
                  },
                }}
              />
            ) : (
              <></>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

const TransactionModal = ({
  isOpen,
  onClose,
  transactionData,
  vendor = "",
  account = "",
  vendorId = null,
  formattedDate,
  customerId,
  accountId,
}) => {
  const contentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [hasXOverflow, setHasXOverflow] = useState(false);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);

  const handleExport = async (format) => {
    try {
      const params = {
        export_format: format,
      };

      const controller = new AbortController();
      const signal = controller.signal;

      const response = await getTransactionsExport(
        customerId,
        formattedDate,
        vendorId,
        accountId,
        params,
        signal
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `vendor Transaction Analysis ${vendor}-${account}.${
        format === "excel" ? "xlsx" : format
      }`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
    }

    const checkOverflow = () => {
      if (contentRef.current && scrollContainerRef.current) {
        const hasOverflowX =
          contentRef.current.scrollWidth > contentRef.current.clientWidth;
        setHasXOverflow(hasOverflowX);

        if (hasOverflowX) {
          const contentWidth = contentRef.current.scrollWidth;
          const visibleWidth = scrollContainerRef.current.clientWidth;

          const thumbWidth = (visibleWidth / contentWidth) * 100;
          setScrollWidth(thumbWidth);
        } else {
          setScrollWidth(0);
        }
      }
    };

    checkOverflow();

    window.addEventListener("resize", checkOverflow);

    return () => {
      observer.disconnect();
    };
  }, [transactionData]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const hasOverflowY =
          contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setHasOverflow(hasOverflowY);

        if (hasOverflowY) {
          const contentHeight = contentRef.current.scrollHeight;
          const visibleHeight = contentRef.current.clientHeight;
          setScrollHeight((visibleHeight / contentHeight) * 100);
        } else {
          setScrollHeight(0);
        }
      }
    };

    checkOverflow();

    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    window.addEventListener("resize", checkOverflow);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [transactionData]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      position={"sticky"}
      scrollBehavior="inside"
      isCentered
      height="100vh"
    >
      <ModalOverlay overflowY={"hidden"} />
      <ModalContent
        borderRadius="12px"
        overflow="hidden"
        maxHeight={"90vh"}
        width="auto"
        maxWidth="unset"
        margin={20}
      >
        <ModalHeader fontWeight="bold" w="100%" p="16px 24px 0px 20px">
          <ModalCloseButton />
          <Flex
            pt={5}
            w="100%"
            align={{ base: "flex-start", md: "flex-end" }}
            justify="space-between"
            direction={{ base: "column", md: "row" }}
            gap={2}
          >
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="bold" fontSize={"24px"}>
                Vendor Transactions
              </Text>
              <Text fontWeight={500} fontSize={"19px"}>
                {vendor} - {account}
              </Text>
            </VStack>

            <Menu isLazy placement="bottom-start" portal={false}>
              <MenuButton
                as={Button}
                leftIcon={<DownloadIcon />}
                bg="#627CFB"
                color="white"
                flexShrink={0}
                minW="fit-content"
                height="36px"
                _hover={{ bg: "#536DEC" }}
                _active={{ bg: "#435CD7" }}
              >
                Export
              </MenuButton>
              <MenuList
                border="1px solid #D1D4DA"
                bg="white"
                h="100%"
                overflowY="auto"
                width="103px"
                minW="unset"
                maxW="170px"
                left="50%"
                transform="translateX(-50%)"
                p={2}
              >
                {["csv", "xlsx", "pdf"].map((ext) => (
                  <MenuItem
                    key={ext}
                    bg="white"
                    color="black"
                    fontWeight="normal"
                    borderRadius="8px"
                    _hover={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    _focus={{
                      color: "white",
                      bg: "#536DEC",
                    }}
                    _active={{
                      color: "white",
                      bg: "#435CD7",
                    }}
                    onClick={() => handleExport(ext)}
                  >
                    .{ext}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>
        </ModalHeader>
        <ModalBody width={"100%"} p="16px 24px 24px 20px">
          <Flex
            flexDirection={"column"}
            position="relative"
            width="100%"
            marginBottom={hasXOverflow ? "20px" : 0}
          >
            <Card
              borderRadius="12px"
              outlineColor={"#D1D4DA"}
              marginRight={hasOverflow ? "20px" : 0}
            >
              <TableContainer
                ref={contentRef}
                maxHeight="542px"
                borderRadius="12px"
                position="relative"
                overflowY={"auto"}
                sx={{
                  "::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
                onWheel={(e) => e.stopPropagation()}
              >
                <Table
                  variant="unstyled"
                  bg="white"
                  padding={0}
                  rounded={"12px"}
                >
                  <Thead position="sticky" top={0} zIndex={0} bg="#171923">
                    <Tr
                      fontSize="18px"
                      sx={{
                        "& td": {
                          color: "white",
                          fontWeight: "200",
                        },
                      }}
                    >
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Text textAlign={"left"} mr={4} fontWeight={700}>
                            {" "}
                            Date
                          </Text>
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Text textAlign={"left"} mr={4} fontWeight={700}>
                            {" "}
                            Description
                          </Text>
                        </Flex>
                      </Td>
                      <Td>
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Text textAlign={"left"} mr={4} fontWeight={700}>
                            {" "}
                            Amount
                          </Text>
                        </Flex>
                      </Td>
                    </Tr>
                  </Thead>

                  <Tbody zIndex={1}>
                    {transactionData?.transactions?.length > 0 ? (
                      transactionData.transactions.map((transaction) => (
                        <Tr key={transaction.id} height={"20px"}>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p={"2px 10px"}
                            h={"33px"}
                          >
                            <HStack>
                              <Flex align="center" justify="center">
                                {transaction.date}
                              </Flex>
                            </HStack>
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p={"2px 10px"}
                            h={"33px"}
                            textAlign={"left"}
                          >
                            {transaction.description}
                          </Td>
                          <Td
                            border="1px solid"
                            borderColor="#D1D4DA"
                            p={"2px 10px"}
                            h={"33px"}
                          >
                            {customCurrencyFormat(
                              transaction.amount,
                              false,
                              true,
                              true,
                              true
                            )}
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr
                        border="1px solid"
                        borderColor="#D1D4DA"
                        height={"20px"}
                      >
                        <Td colSpan={7} textAlign="center">
                          No transaction data.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>

                  <Tfoot position="sticky" bottom={0} zIndex={0} bg="#171923">
                    <Tr
                      fontSize="18px"
                      sx={{
                        "& td": {
                          color: "white",
                          fontWeight: "200",
                        },
                      }}
                    >
                      <Td>Total</Td>
                      <Td></Td>
                      <Td textAlign="right">
                        {customCurrencyFormat(
                          transactionData?.total_amount
                            ? transactionData?.total_amount
                            : "0",
                          false,
                          true,
                          true,
                          true
                        )}
                      </Td>
                    </Tr>
                  </Tfoot>
                </Table>
              </TableContainer>
            </Card>
            {hasOverflow ? (
              <Box>
                <Scrollbar
                  targetRef={contentRef}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: "100%",
                    visibility: hasOverflow ? "visible" : "hidden",
                    "&::-webkit-scrollbar-thumb": {
                      height: `${scrollHeight}%`,
                      visibility: hasOverflow ? "visible" : "hidden",
                    },
                    "&::-webkit-scrollbar-track": {
                      visibility: hasOverflow ? "visible" : "hidden",
                    },
                  }}
                />
              </Box>
            ) : (
              <></>
            )}
          </Flex>
          <Box position={"relative"} width={"100%"} ref={scrollContainerRef}>
            {hasXOverflow ? (
              <HorizontalScrollbar
                targetRef={contentRef}
                sx={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: "100%",
                  height: "100%",
                  visibility: hasXOverflow ? "visible" : "hidden",
                  "&::-webkit-scrollbar-thumb": {
                    width: `${scrollWidth}%`,
                    visibility: hasXOverflow ? "visible" : "hidden",
                  },
                  "&::-webkit-scrollbar-track": {
                    visibility: hasXOverflow ? "visible" : "hidden",
                  },
                }}
              />
            ) : (
              <></>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const VendorAnalytics = () => {
  const [isMobile] = useMediaQuery("(max-width: 1280px)");

  const { profile } = useCurrentProfile();
  const [activeCustomer, setActiveCustomer] = useState(null);

    useEffect(() => {
      if (!profile) return;

      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      if (profile.type !== "admin") {
        const customer =
          profile.customers.find((c) => c.id.toString() === storedId) ||
          profile.customers[0] ||
          null;
        setActiveCustomer(customer);
        return;
      }

      const fetchCustomer = async () => {
        try {
          const res = await axios.get(
            `/api/v2/customers/${storedId}/`,
            { headers: { "Content-Type": "application/json" } },
            { withCredentials: true }
          );
          setActiveCustomer(res.data);
        } catch (err) {
          console.error("Error fetching admin customer:", err);
        }
      };

      fetchCustomer();
    }, [profile]);

    useEffect(() => {
    const handleSelectedChange = async () => {
      const storedId = sessionStorage.getItem("selectedCustomerId");
      if (!storedId) return;

      if (profile.type !== "admin") {
        const found = profile.customers.find((c) => c.id.toString() === storedId);
        if (found) setActiveCustomer(found);
      } else {
        try {
          const res = await axios.get(`/api/v2/customers/${storedId}/`, {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          });
          setActiveCustomer(res.data);
        } catch (err) {
          console.error("Error fetching admin customer:", err);
        }
      }
    };

    window.addEventListener("selectedCustomerId-changed", handleSelectedChange);
    return () => {
      window.removeEventListener("selectedCustomerId-changed", handleSelectedChange);
    };
  }, [profile]);

  const customer = activeCustomer;
  const customer_id = activeCustomer?.id;

  // Initial and current dash date
   const normalizeToEOM = (date) => {
      if (!date) return fallbackDate;
      return endOfMonth(new Date(date));
    };

    const defaultDate = customer?.dashboard_date
      ? normalizeToEOM(parseISO(customer.dashboard_date))
      : fallbackDate;

    const [initialDashDate, setInitialDashDate] = useState(() =>
      normalizeToEOM(getCustomerDashboardDate(customer))
    );

    const [dashDate, setDashDate] = useState(initialDashDate);

    const [dateCategory, setDateCategory] = useState(() =>
      format(initialDashDate, "yyyy-MM")
    );


    useEffect(() => {
      if (!activeCustomer) return;

      const stored = localStorage.getItem("dashboardDate");
      const newDate = stored
        ? normalizeToEOM(new Date(stored.split("-").map(Number)))
        : normalizeToEOM(getCustomerDashboardDate(activeCustomer));

      setDashDate(newDate);
      setInitialDashDate(newDate);
      setDateCategory(format(newDate, "yyyy-MM"));
    }, [activeCustomer]);


    useEffect(() => {
      const handleDashboardDateChange = () => {
        if (!activeCustomer) return;

        const stored = localStorage.getItem("dashboardDate");
        const newDate = stored
          ? normalizeToEOM(new Date(stored.split("-").map(Number)))
          : normalizeToEOM(getCustomerDashboardDate(activeCustomer));

        setDashDate(newDate);
        setInitialDashDate(newDate);
        setDateCategory(format(newDate, "yyyy-MM"));
      };

      window.addEventListener("dashboardDate-changed", handleDashboardDateChange);
      return () =>
        window.removeEventListener(
          "dashboardDate-changed",
          handleDashboardDateChange
        );
    }, [activeCustomer]);

    // Controlled date update function
    const updateDate = (newDate) => {
      const normalized = normalizeToEOM(newDate);
      setDashDate(normalized);
      setDateCategory(format(normalized, "yyyy-MM"));
    };

  return (
    <ClientDashboardLayout>
      <ChakraProvider theme={theme}>
        <Stack bg="#F4F4F2" overflowX={"clip"} minH={"100vh"}>
          {isMobile && (
            <Card
              position="fixed"
              zIndex={302}
              borderRadius={0}
              h="72px"
              borderBottom="1px solid #e0e0e0"
              width="100%"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                h={"100%"}
                w="100%"
                px={4}
              >
                <Button
                  bg="white"
                  _hover={{ color: "white" }}
                  _active={{ color: "white" }}
                  w="50px"
                  h="50px"
                  p={2}
                  onClick={() => {
                    window.dispatchEvent(new Event("sidebar:expand"));
                  }}
                >
                  <MenuIcon
                    style={{ width: "50px", height: "100%" }}
                    color="black"
                  />
                </Button>
              </HStack>
            </Card>
          )}
          {!isMobile && (
            <Card
              position="fixed"
              mt={isMobile ? "72px" : "0px"}
              borderRadius={0}
              width={isMobile ? "100%" : "auto"}
              height="75px"
              display="flex"
              alignItems="center"
              zIndex={600}
              borderBottom="1px solid #e0e0e0"
              mb={1}
            >
              <Grid
                templateColumns="1fr 200fr 1fr"
                width="100%"
                maxW={"100%"}
                alignItems="center"
                padding="0px 20px"
                height="100%"
              >
                <Box
                  gridRow={1}
                  gridColumn={1}
                  textAlign={"left"}
                  fontWeight={700}
                  fontSize={"26px"}
                  whiteSpace={"nowrap"}
                >
                  Vendor Analysis
                </Box>
              </Grid>
            </Card>
          )}
          <Box mt={"75px"} p={"32px 24px"}>
            <VendorTable
              customer_id={customer_id}
              dashDate={dashDate}
              updateDate={updateDate}
              dateCategory={dateCategory}
              setDateCategory={setDateCategory}
              initialDashDate={defaultDate}
            />
          </Box>
        </Stack>
      </ChakraProvider>
    </ClientDashboardLayout>
  );
};

export default VendorAnalytics;
